
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";

type DeviceFingerprint = Database['public']['Tables']['device_fingerprints']['Row'];

export const DeviceManager = () => {
  const [devices, setDevices] = useState<DeviceFingerprint[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchDevices = async () => {
    try {
      const { data, error } = await supabase
        .from('device_fingerprints')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDevices(data || []);
    } catch (error) {
      console.error('Fejl ved hentning af enheder:', error);
      toast({
        title: "Fejl ved hentning af enheder",
        description: "Der skete en fejl. Prøv igen senere.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateDeviceStatus = async (deviceId: string, newStatus: Database['public']['Enums']['device_status']) => {
    try {
      const { error } = await supabase
        .from('device_fingerprints')
        .update({ status: newStatus })
        .eq('id', deviceId);

      if (error) throw error;

      toast({
        title: "Status opdateret",
        description: `Enhedens status er blevet opdateret til ${newStatus}`,
      });

      fetchDevices(); // Genindlæs listen
    } catch (error) {
      console.error('Fejl ved opdatering af enhedsstatus:', error);
      toast({
        title: "Fejl ved opdatering",
        description: "Der skete en fejl. Prøv igen senere.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'trusted':
        return <Badge className="bg-green-500">Godkendt</Badge>;
      case 'blocked':
        return <Badge variant="destructive">Blokeret</Badge>;
      case 'pending':
        return <Badge variant="secondary">Afventer</Badge>;
      default:
        return <Badge variant="outline">Ukendt</Badge>;
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Indlæser enheder...</div>;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Bruger Agent</TableHead>
            <TableHead>Første Gang Set</TableHead>
            <TableHead>Sidst Set</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Handlinger</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device) => (
            <TableRow key={device.id}>
              <TableCell className="font-mono text-sm">
                {device.user_agent}
              </TableCell>
              <TableCell>
                {new Date(device.first_seen || '').toLocaleDateString('da-DK')}
              </TableCell>
              <TableCell>
                {new Date(device.last_seen || '').toLocaleDateString('da-DK')}
              </TableCell>
              <TableCell>
                {getStatusBadge(device.status)}
              </TableCell>
              <TableCell className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateDeviceStatus(device.id, 'trusted')}
                  disabled={device.status === 'trusted'}
                >
                  Godkend
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateDeviceStatus(device.id, 'blocked')}
                  disabled={device.status === 'blocked'}
                  className="bg-red-100 hover:bg-red-200"
                >
                  Bloker
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
